import { useState, useEffect, useMemo } from 'react';
import useScopedTranslation from 'hooks/useScopedTranslation';
import { taskNotifI18n } from './i18n';
import { useDesignSystem } from '../../context/DesignSystemContext';
import {
  getTaskConfiguratorList,
  getBusinessAreas,
  getLegalNewsTemplates,
  getSendMethods,
} from '../../services/taskNotificationsService';
import { getCurrentUser } from '../../services/authService';

export default function CreateEditTaskNotificationsModal({
  show,
  mode = 'create',
  initialData = null,
  onClose,
  onSave,
}) {
  const { Modal, Dropdown, ComboBox, Input } = useDesignSystem();
  const { t, language: tnLanguage } = useScopedTranslation(taskNotifI18n);

  const statusOptions = useMemo(
    () => [
      { label: t('tn_status_active'), value: true },
      { label: t('tn_status_inactive'), value: false },
    ],
    [tnLanguage]
  );

  const [taskOptions, setTaskOptions] = useState([]);
  const [areaOptions, setAreaOptions] = useState([]);
  const [templateOptions, setTemplateOptions] = useState([]);
  const [sendMethods, setSendMethods] = useState([]);

  const [form, setForm] = useState({
    taskConfiguratorId: null,
    businessAreaId: null,
    templateId: null,
    sendMethodId: null,
    sendOnStart: false,
    sendOnEnd: false,
    isActive: true,
    subject: '',
  });

  const [isSaving, setIsSaving] = useState(false);

  // ---------------------------------------------------------------------
  // 🔥 Cargar listas + precargar valores de edición (orden correcto)
  // ---------------------------------------------------------------------
  useEffect(() => {
    if (!show) return;

    const loadAll = async () => {
      const tasks = await getTaskConfiguratorList();
      const areas = await getBusinessAreas();
      const templates = await getLegalNewsTemplates();
      const methods = await getSendMethods();

      setTaskOptions(tasks.map((t) => ({ label: t.name, value: t.id })));
      setAreaOptions(areas.map((a) => ({ label: a.name, value: a.id })));
      setTemplateOptions(
        templates.map((t) => ({
          label: t.name,
          value: t.id,
          messageType: t.deliveryMethod,
        }))
      );
      setSendMethods(methods.map((m) => ({ label: m.name, value: m.id })));

      // ⭐ Precargar SOLO después de cargar todas las listas
      if (mode === 'edit' && initialData) {
        setForm({
          taskConfiguratorId: initialData.taskConfiguratorId,
          businessAreaId: initialData.businessAreaId,
          templateId: initialData.templateId,
          sendMethodId: initialData.sendMethodId,
          sendOnStart: initialData.sendOnStart,
          sendOnEnd: initialData.sendOnEnd,
          isActive: initialData.active,
          subject: initialData.subject,
        });
      }
    };

    loadAll();
  }, [show, mode, initialData]);

  // ---------------------------------------------------------------------
  // Handlers
  // ---------------------------------------------------------------------
  const handleChange = (field) => (opt) => {
    if (opt?.target) {
      const value = opt.target.value;
      setForm((prev) => ({
        ...prev,
        [field]: value,
      }));
      return;
    }
    setForm((prev) => ({
      ...prev,
      [field]: opt?.value ?? opt,
    }));
  };

  // ---------------------------------------------------------------------
  // Validación
  // ---------------------------------------------------------------------
  const isValid =
    form.taskConfiguratorId &&
    form.businessAreaId &&
    form.templateId &&
    form.sendMethodId;

  // ---------------------------------------------------------------------
  // Guardar
  // ---------------------------------------------------------------------
  const handleSubmit = async () => {
    if (!isValid) return;

    setIsSaving(true);

    try {
      const user = getCurrentUser();
      const creatorId = user?.userId;

      if (!creatorId) {
        alert(t('tn_modal_err_noUser'));
        return;
      }

      const payload = {
        id: initialData?.id ?? 0,
        taskConfiguratorId: form.taskConfiguratorId,
        businessAreaId: form.businessAreaId,
        templateId: form.templateId,
        sendMethodId: form.sendMethodId,
        sendOnStart: form.sendOnStart,
        sendOnEnd: form.sendOnEnd,
        isActive: form.isActive,
        creatorId: creatorId, // ⭐ requerido por backend
        subject: form.subject,
      };

      await onSave(payload);
    } finally {
      setIsSaving(false);
    }
  };

  // ---------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------
  const title =
    mode === 'edit' ? t('tn_modal_titleEdit') : t('tn_modal_titleCreate');

  const subtitle =
    mode === 'edit' ? t('tn_modal_subtitleEdit') : t('tn_modal_subtitleCreate');

  return (
    <Modal
      title={title}
      subtitle={subtitle}
      show={show}
      onClose={onClose}
      primaryBtnTxt={
        mode === 'edit' ? t('tn_modal_save') : t('tn_modal_create')
      }
      primaryBtnAction={handleSubmit}
      disabledPrimaryBtn={isSaving || !isValid}
      secondaryBtnTxt={t('tn_modal_cancel')}
      secondaryBtnAction={onClose}
      width='720px'
      ctaAligment='right'
    >
      <div className='row'>
        {/* Tarea */}
        <div className='col-12 col-md-6'>
          <ComboBox
            label={t('tn_modal_taskLabel')}
            options={taskOptions}
            placeholder={t('tn_modal_taskPlaceholder')}
            value={
              taskOptions.find((o) => o.value === form.taskConfiguratorId) ||
              null
            }
            onChange={handleChange('taskConfiguratorId')}
            width='100%'
            className='space-like-input'
          />
        </div>

        <div className='col-12 col-md-6'>
          <Dropdown
            label={t('tn_modal_areaLabel')}
            placeholder={t('tn_modal_areaPlaceholder')}
            options={areaOptions}
            value={
              areaOptions.find((o) => o.value === form.businessAreaId) || null
            }
            onChange={handleChange('businessAreaId')}
            width='100%'
            className='space-like-input'
          />
        </div>

        <div className='col-12 col-md-6'>
          <Dropdown
            label={t('tn_modal_sendMethodLabel')}
            placeholder={t('tn_modal_sendMethodPlaceholder')}
            options={sendMethods}
            value={
              sendMethods.find((o) => o.value === form.sendMethodId) || null
            }
            onChange={handleChange('sendMethodId')}
            width='100%'
            className='space-like-input'
          />
        </div>

        {/* Plantilla */}
        <div className='col-12 col-md-6'>
          <ComboBox
            label={t('tn_modal_templateLabel')}
            placeholder={t('tn_modal_templatePlaceholder')}
            options={templateOptions}
            value={
              templateOptions.find((o) => o.value === form.templateId) || null
            }
            onChange={handleChange('templateId')}
            width='100%'
            className='space-like-input'
          />
        </div>

        {/* Estado */}
        <div className='col-12 col-md-6'>
          <Dropdown
            label={t('tn_modal_statusLabel')}
            options={statusOptions}
            value={statusOptions.find((o) => o.value === form.isActive)}
            onChange={(opt) =>
              setForm((prev) => ({ ...prev, isActive: opt.value }))
            }
            width='100%'
            className='space-like-input'
          />
        </div>

        <div className='col-12 col-md-6'>
          <Input
            label={t('tn_modal_subjectLabel')}
            value={form.subject}
            onChange={handleChange('subject')}
          />
        </div>

        <div className='col-12 col-md-6'>
          <div
            className={`p-3 border rounded-3 d-flex gap-3 cursor-pointer ${
              form.sendOnStart ? 'bg-light' : ''
            }`}
            onClick={() =>
              setForm((prev) => ({ ...prev, sendOnStart: !prev.sendOnStart }))
            }
          >
            <input
              type='checkbox'
              checked={form.sendOnStart}
              onChange={(e) => e.stopPropagation()}
            />
            <div>
              <p className='fw-semibold mb-1'>
                {t('tn_modal_sendOnStartLabel')}
              </p>
              <p className='mini font-light mb-0'>
                {t('tn_modal_sendOnStartHelp')}
              </p>
            </div>
          </div>
        </div>

        {/* Switch Final */}
        <div className='col-12 col-md-6'>
          <div
            className={`p-3 border rounded-3 d-flex gap-3 cursor-pointer ${
              form.sendOnEnd ? 'bg-light' : ''
            }`}
            onClick={() =>
              setForm((prev) => ({ ...prev, sendOnEnd: !prev.sendOnEnd }))
            }
          >
            <input
              type='checkbox'
              checked={form.sendOnEnd}
              onChange={(e) => e.stopPropagation()}
            />
            <div>
              <p className='fw-semibold mb-1'>{t('tn_modal_sendOnEndLabel')}</p>
              <p className='mini font-light mb-0'>
                {t('tn_modal_sendOnEndHelp')}
              </p>
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
}
